import { getActivitySessionById } from "@/actions/activity-session";
import { checkBuddyStatus } from "@/actions/buddy";
import {
  getChatInfo,
  getDirectChatParticipant,
  getGroupMembers,
  getMessageSeenBy,
} from "@/actions/chat";
import { getEventChatData, getEventOccurrenceChatData } from "@/actions/event";
import { getHiiveById } from "@/actions/hiive";
import { listSuggestedPeople } from "@/actions/social-suggestions";
import {
  getChatMessages,
  getChatMessagesDelta,
  getTotalUnreadChatCount,
  getTypingUsers,
  getUserChats,
  getUserChatsDelta,
  getUsersPresence,
} from "@/lib/chat/chat-rpc";
import type { ChatSuggestedSurface } from "./keys";

/** Newest messages a thread opens with and every full sync refreshes. */
const CHAT_LIVE_WINDOW_SIZE = 150;

const coerceDate = (value: unknown) => {
  if (value instanceof Date) return value;
  if (typeof value === "string" || typeof value === "number") {
    return new Date(value);
  }
  return null;
};

const normalizeChatListRows = <
  T extends {
    updatedAt?: Date | string | number | null;
    pinnedAt?: Date | string | number | null;
    mutedUntil?: Date | string | number | null;
    presence?: {
      lastActiveAt?: Date | string | number | null;
    } | null;
    readState?: {
      lastSeenAt?: Date | string | number | null;
    } | null;
    lastMessage?: {
      createdAt?: Date | string | number | null;
    } | null;
    lastReaction?: {
      createdAt?: Date | string | number | null;
    } | null;
  },
>(
  rows: T[],
) =>
  rows.map((row) => ({
    ...row,
    updatedAt: coerceDate(row.updatedAt) ?? new Date(0),
    pinnedAt: coerceDate(row.pinnedAt),
    mutedUntil: coerceDate(row.mutedUntil),
    presence: row.presence
      ? {
          ...row.presence,
          observedAt: Date.now(),
          lastActiveAt: coerceDate(row.presence.lastActiveAt),
        }
      : null,
    readState: row.readState
      ? {
          ...row.readState,
          lastSeenAt: coerceDate(row.readState.lastSeenAt),
        }
      : null,
    lastMessage: row.lastMessage
      ? {
          ...row.lastMessage,
          createdAt: coerceDate(row.lastMessage.createdAt) ?? new Date(0),
        }
      : null,
    lastReaction: row.lastReaction
      ? {
          ...row.lastReaction,
          createdAt: coerceDate(row.lastReaction.createdAt) ?? new Date(0),
        }
      : null,
  }));

const isAuthError = (message: string | undefined) =>
  Boolean(message && /auth|signed in|unauthorized/i.test(message));

export const chatQueryFns = {
  userChats: async (tab: "all" | "archived" = "all") => {
    const result = await getUserChats({ tab });

    if (result.error) {
      throw new Error(result.error);
    }

    return normalizeChatListRows(result.data ?? []);
  },
  userChatsDelta: async (input: {
    tab: "all" | "archived";
    afterUpdatedAt?: Date;
    limit?: number;
  }) => {
    const result = await getUserChatsDelta({
      tab: input.tab,
      afterUpdatedAt: input.afterUpdatedAt,
      limit: input.limit,
    });
    if (result.error) {
      throw new Error(result.error);
    }
    return normalizeChatListRows(result.data ?? []);
  },
  totalUnreadCount: async () => {
    const result = await getTotalUnreadChatCount();

    if ("error" in result) {
      if (isAuthError(result.error)) return 0;
      throw new Error(result.error);
    }

    return result.data;
  },
  chatInfo: async (chatId: number) => {
    const result = await getChatInfo(chatId);

    if (result.error || !result.data) {
      throw new Error(result.error ?? "Failed to load chat");
    }

    return result.data;
  },
  chatMessages: async (chatId: number) => {
    // The live window. Older history pages in on scroll-back
    // (chatMessagesBefore), so opening a chat never pays for 500 rows.
    const result = await getChatMessages(chatId, CHAT_LIVE_WINDOW_SIZE);

    if (result.error) {
      throw new Error(result.error);
    }

    return (result.data ?? []).map((message) => ({
      ...message,
      createdAt: new Date(message.createdAt),
      editedAt: message.editedAt ? new Date(message.editedAt) : null,
      readState: message.readState
        ? {
            ...message.readState,
            lastSeenAt: message.readState.lastSeenAt
              ? new Date(message.readState.lastSeenAt)
              : null,
          }
        : null,
    }));
  },
  /**
   * One page of history strictly older than the (createdAt, id) cursor, for
   * scrolling back past the thread's latest window.
   */
  chatMessagesBefore: async (input: {
    chatId: number;
    beforeCreatedAt: Date;
    beforeMessageId: number;
    limit: number;
  }) => {
    const result = await getChatMessages(input.chatId, input.limit, 0, {
      beforeCreatedAt: input.beforeCreatedAt,
      beforeMessageId: input.beforeMessageId,
    });

    if (result.error) {
      throw new Error(result.error);
    }

    const data = result.data ?? [];
    return {
      hasMore: result.hasMore ?? data.length >= input.limit,
      messages: data.map((message) => ({
        ...message,
        createdAt: new Date(message.createdAt),
        editedAt: message.editedAt ? new Date(message.editedAt) : null,
        readState: message.readState
          ? {
              ...message.readState,
              lastSeenAt: message.readState.lastSeenAt
                ? new Date(message.readState.lastSeenAt)
                : null,
            }
          : null,
      })),
    };
  },
  chatMessagesDelta: async (input: {
    chatId: number;
    afterCreatedAt?: Date;
    afterMessageId?: number;
    afterUpdatedAt?: Date;
    limit?: number;
  }) => {
    const result = await getChatMessagesDelta(input);
    if ("error" in result && result.error) {
      throw new Error(result.error);
    }
    return (result.data ?? []).map((message) => ({
      ...message,
      createdAt: new Date(message.createdAt),
      editedAt: message.editedAt ? new Date(message.editedAt) : null,
      readState: message.readState
        ? {
            ...message.readState,
            lastSeenAt: message.readState.lastSeenAt
              ? new Date(message.readState.lastSeenAt)
              : null,
          }
        : null,
    }));
  },
  directChatParticipant: async (chatId: number, currentUserId: string) => {
    const result = await getDirectChatParticipant(chatId, currentUserId);

    if (result.error) {
      throw new Error(result.error);
    }

    return result.data ?? null;
  },
  typingUsers: async (chatId: number) => {
    const result = await getTypingUsers(chatId);
    if (result.error) {
      throw new Error(result.error);
    }
    return result.data ?? [];
  },
  messageSeenBy: async (messageId: number) => {
    const result = await getMessageSeenBy(messageId);
    if (result.error) {
      throw new Error(result.error);
    }
    return result.data ?? [];
  },
  groupMembers: async (chatId: number) => {
    const result = await getGroupMembers(chatId);
    if (result.error) {
      throw new Error(result.error);
    }
    return result.data ?? [];
  },
  usersPresence: async (userIds: string[]) => {
    const result = await getUsersPresence({ userIds });
    if (result.error) {
      throw new Error(result.error);
    }
    return (result.data ?? []).map((row) => ({
      ...row,
      lastActiveAt: coerceDate(row.lastActiveAt),
      observedAt: Date.now(),
    }));
  },
  buddyStatus: async (targetUserId: string) => {
    const result = await checkBuddyStatus(targetUserId);

    if (!result.ok) {
      throw new Error(result.message ?? "Failed to check buddy status");
    }

    return result.data;
  },
  hiiveById: async (hiiveId: number) => {
    const result = await getHiiveById(hiiveId);

    if (result.error || !result.data) {
      throw new Error(result.error ?? "Failed to load hiive");
    }

    return result.data;
  },
  activitySessionById: async (activitySessionId: number) => {
    const result = await getActivitySessionById(activitySessionId);

    if (!result.ok) {
      throw new Error(result.message ?? "Failed to load activity session");
    }

    if (!result.data) {
      throw new Error("Failed to load activity session");
    }

    return result.data;
  },
  eventOccurrenceChatData: async (occurrenceId: number) => {
    const result = await getEventOccurrenceChatData(occurrenceId);

    if (!result.ok || !result.data) {
      throw new Error(result.message ?? "Failed to load event occurrence");
    }

    return result.data;
  },
  eventChatData: async (eventId: number) => {
    const result = await getEventChatData(eventId);

    if (!result.ok || !result.data) {
      throw new Error(result.message ?? "Failed to load event");
    }

    return result.data;
  },
  suggestedPeople: async (input: {
    surface: ChatSuggestedSurface;
    limit: number;
  }) => {
    const result = await listSuggestedPeople({
      surface: input.surface,
      limit: input.limit,
    });

    if (!result.ok) {
      throw new Error(result.message ?? "Failed to load suggested people");
    }

    return result.data;
  },
};
